import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import {
  wsConnect,
  wsConnected,
  wsDisconnect,
  wsDisconnected,
  newMessage,
  updateMessage,
  updateConnectedUsers,
  updateAlarm,
  updateTimeWarningAlarm,
  updateTraceData,
  updateTimeshareStatus,
  updateTimeshareUsers,
  testConnection,
  testEmail,
  testSnmp,
  subscribeToAnalyzers,
  joinTimeshare,
  leaveTimeshare,
  joinWatchTimeshare,
  leaveWatchTimeshare,
  bootTimeshareUser,
  unsubscribeFromAllAnalyzers,
  subscribeToCommands,
  updateAnalyzerProperties,
  updateMinMax,
  resetMinMax,
  sendScpiCommand,
  requestUsers,
  updateEmailTestStatus,
} from './spectrasWebSocketServerSlice';

let WS_HOST: string;
if (process.env.NODE_ENV !== 'production') {
  WS_HOST = 'ws://localhost:5025';
} else {
  WS_HOST = `ws://${window.location.host}/ws`;
  if (window.location.protocol === 'https:') {
    WS_HOST = `wss://${window.location.host}/ws`;
  }
}

let messageQueue: any = [];
let messageQueueInterval: any = undefined;

const wsMiddleware = () => {
  let socket: WebSocket | null = null;
  const decoder = new TextDecoder();

  const onOpen = (store: MiddlewareAPI<Dispatch<AnyAction>>) => () => {
    store.dispatch(wsConnected());
  };

  const onClose = (store: MiddlewareAPI<Dispatch<AnyAction>>) => () => {
    store.dispatch(wsDisconnect());
    setTimeout(() => store.dispatch(wsConnect()), 5000);
  };

  const onError = () => () => {};

  const onMessage =
    (store: MiddlewareAPI<Dispatch<AnyAction>>) => (event: any) => {
      let isTraceData = false;
      let payload;

      try {
        payload = JSON.parse(decoder.decode(event.data));
      } catch (e) {
        isTraceData = true;
      }

      if (isTraceData) {
        messageQueue.push(event.data);
      } else if (payload?.type === 'test_connection') {
        const message = {
          analyzer_id: payload.analyzer_id,
          command: payload.command,
          message: payload.data,
          type: payload.type,
        };

        store.dispatch(updateMessage(message));
      } else if (payload?.type === 'validated_snapshot') {
        const message = {
          analyzer_id: payload.analyer_id,
          snapshot_id: payload.snapshot_id,
          type: payload.type,
          message: payload.data,
        };
        store.dispatch(updateMessage(message));
      } else if (
        payload?.type === 'scpi_command' ||
        payload?.type === 'minmax_reset'
      ) {
        const message = {
          analyzer_id: payload.analyzer_id,
          snapshot_id: payload?.snapshot_id,
          command: payload.command,
          message: payload.data,
          type: payload.type,
        };
        store.dispatch(updateMessage(message));
      } else if (payload?.type === 'alarm') {
        const alarmMessage = {
          analyzer_id: payload.analyzer_id,
          alarm_log_id: payload.alarm_log_id,
          alarm_log_items: payload.alarm_log_items,
          message: payload.data,
          name: payload.name,
          timestamp: payload.timestamp,
          ignore_interval: payload.ignore_interval,
          stopped: payload.stopped,
          start_mode: payload.start_mode,
          stop_mode: payload.stop_mode,
          alarm_id: payload.alarm_id,
          warning: payload.warning,
        };
        store.dispatch(updateAlarm(alarmMessage));
      } else if (payload?.type === 'time_warning_alarm') {
        const timeWarningAlarmMessage = {
          analyzerName: payload.analyzerName,
          criticalThreshold: payload.criticalThreshold,
          timestamp: payload.timestamp,
        };
        store.dispatch(updateTimeWarningAlarm(timeWarningAlarmMessage));
      } else if (payload?.type === 'reported_sweep_time') {
        const sweepTimeMessage = {
          analyzer_id: payload.analyzer_id,
          snapshot_id: payload?.snapshot_id,
          command: payload.command,
          message: payload.data,
          type: payload.type,
        };
        store.dispatch(updateMessage(sweepTimeMessage));
      } else if (payload?.type === 'request_users') {
        const userMessage = {
          userIds: payload.user_ids,
          type: payload.type,
        };
        store.dispatch(updateConnectedUsers(userMessage));
      } else if (payload?.type === 'timeShare') {
        const timeshareMessage = {
          action: payload.action,
          users: payload?.users,
        };
        if (payload.action === 'usersUpdated') {
          store.dispatch(updateTimeshareUsers(timeshareMessage));
        } else {
          store.dispatch(updateTimeshareStatus(timeshareMessage));
        }
      } else if (payload?.type === 'email_tested') {
        store.dispatch(updateEmailTestStatus(payload));
      } else {
        return;
      }
    };

  // the middleware part of this function
  const middlewareToReturn: Middleware = (store) => (next) => (action) => {
    const { payload } = action;

    switch (action.type) {
      case wsConnect.type:
        if (socket?.readyState === WebSocket.OPEN) return;
        // connect to the remote host
        socket = new WebSocket(WS_HOST);
        socket.binaryType = 'arraybuffer';
        // websocket handlers
        socket.onmessage = onMessage(store);
        socket.onclose = onClose(store);
        socket.onopen = onOpen(store);
        socket.onerror = onError();

        if (messageQueueInterval !== undefined) {
          clearInterval(messageQueueInterval);
        }

        messageQueueInterval = setInterval(() => {
          if (messageQueue.length > 0) {
            store.dispatch(updateTraceData(messageQueue));
          }
          messageQueue = [];
        }, 60);

        break;
      case wsDisconnect.type:
        if (socket) {
          socket.close();
        }
        socket = null;
        store.dispatch(wsDisconnected());
        break;
      case newMessage.type:
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify(action.payload));
        break;
      case testEmail.type:
        const testEmailMessage = JSON.stringify({
          type: 'email_test',
          user_id: payload.user_id,
          email_settings: payload.email_settings,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(testEmailMessage);
        break;
      case testSnmp.type:
        const testSnmpMessage = JSON.stringify({
          type: 'snmp_test',
          snmp_settings: payload,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(testSnmpMessage);
        break;
      case testConnection.type:
        const message = JSON.stringify({
          type: 'test_connection',
          instrument: payload.instrument,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(message);
        break;
      case subscribeToAnalyzers.type:
        const subscribeMessage = JSON.stringify({
          type: 'subscribe',
          channel: 'tracedata',
          analyzers: payload.analyzer_ids,
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(subscribeMessage);
        break;
      case joinTimeshare.type:
        const joinTimeshareMessage = JSON.stringify({
          type: 'joinTimeshare',
          user: payload.user_id,
          snapshotId: payload.snapshot_id,
          analyzerId: payload.analyzer_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(joinTimeshareMessage);
        break;
      case leaveTimeshare.type:
        const leaveTimeshareMessage = JSON.stringify({
          type: 'leaveTimeshare',
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(leaveTimeshareMessage);
        break;
      case joinWatchTimeshare.type:
        const watchTimeshareMessage = JSON.stringify({
          type: 'watchTimeshare',
          user: payload.user_id,
          analyzerId: payload.analyzer_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(watchTimeshareMessage);
        break;
      case leaveWatchTimeshare.type:
        const leaveWatchTimeshareMessage = JSON.stringify({
          type: 'leaveWatchTimeshare',
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(leaveWatchTimeshareMessage);
        break;
      case bootTimeshareUser.type:
        const bootTimeshareUserMessage = JSON.stringify({
          type: 'bootTimeshareUser',
          user: payload.user_id,
          userToBoot: payload.user_id_to_boot,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(bootTimeshareUserMessage);
        break;
      case subscribeToCommands.type:
        const commandSubscribeMessage = JSON.stringify({
          type: 'subscribe',
          channel: 'commandchannel',
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(commandSubscribeMessage);
        break;
      case unsubscribeFromAllAnalyzers.type:
        const unsubscribeMessage = JSON.stringify({
          type: 'unsubscribe_from_all',
          channel: 'tracedata',
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(unsubscribeMessage);
        break;
      case updateAnalyzerProperties.type:
        const commandMessage = JSON.stringify({
          type: 'command',
          properties: payload.properties,
          analyzer: payload.analyzer,
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(commandMessage);
        break;
      case updateMinMax.type:
        const minMaxMessage = JSON.stringify({
          type: 'minmax_changed',
          properties: payload.properties,
          analyzer: payload.analyzer,
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(minMaxMessage);
        break;
      case resetMinMax.type:
        const restMinMaxMessage = JSON.stringify({
          type: 'minmax_reset',
          properties: {},
          analyzer: payload.analyzer,
          snapshot_id: payload.snapshot_id,
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(restMinMaxMessage);
        break;
      case sendScpiCommand.type:
        const scpiMessage = JSON.stringify({
          type: 'scpi_command',
          analyzer: payload.analyzer,
          scpi_command: payload.scpiCommand,
        });
        socket?.send(scpiMessage);
        break;
      case requestUsers.type:
        const userRequestMessage = JSON.stringify({
          type: 'request_users',
          user: payload.user_id,
        });
        if (socket?.readyState !== WebSocket.OPEN) {
          setTimeout(() => {
            if (socket?.readyState !== WebSocket.OPEN) return;
            socket?.send(userRequestMessage);
          }, 500);
          return;
        }
        socket?.send(userRequestMessage);
        break;
      default:
        return next(action);
    }
  };
  return middlewareToReturn;
};

export default wsMiddleware();
